import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, ShieldCheck, FileText, Truck, CheckCircle2, Minus, Plus, Pencil } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import { resolveUrl } from '../api';
import EditableImage from '../components/EditableImage';
import { ProductImage } from '../components/VialIllustration';

const ProductPage = () => {
  const { slug } = useParams();
  const { products, updateProduct, editMode } = useCMS();
  const { isAuthed } = useAuth();
  const product = products.find((p) => p.slug === slug);
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  if (!product) {
    return (
      <main className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h1 className="text-3xl font-bold text-[#0a0a0a]">Product not found</h1>
        <Link to="/shop" className="text-red-700 hover:text-red-600 font-semibold mt-4 inline-block">Back to shop →</Link>
      </main>
    );
  }

  const editable = isAuthed && editMode;
  const related = products.filter((p) => p.id !== product.id).slice(0, 4);

  const blurUpdate = (field, parser = (v) => v) => async (e) => {
    const v = parser(e.target.innerText);
    if (v != null && v !== product[field]) await updateProduct(product.id, { [field]: v });
  };

  return (
    <main className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-700 mb-2 transition-colors">
          <ArrowLeft size={15} /> Back to All Products
        </Link>
        {isAuthed && (
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100">
            <Pencil size={12} /> Edit in dashboard
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-6">
        <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-10 flex items-center justify-center aspect-square relative">
          {editable ? (
            <EditableImage
              src={product.image}
              alt={product.name}
              className="max-w-[70%] max-h-full object-contain mx-auto"
              onUpload={(url) => updateProduct(product.id, { image: url })}
            />
          ) : (
            <ProductImage
              product={product}
              className="max-w-[80%] max-h-full object-contain mx-auto"
              containerClassName="w-full h-full flex items-center justify-center p-4"
            />
          )}
        </div>

        <div>
          <div className="text-xs font-semibold tracking-[0.14em] uppercase text-red-700">{product.category}</div>
          <h1
            className={`text-[#0a0a0a] font-bold text-4xl md:text-5xl tracking-tight mt-2 ${editable ? 'cms-editable' : ''}`}
            contentEditable={editable} suppressContentEditableWarning onBlur={blurUpdate('name')}
          >{product.name}</h1>
          <div className="flex items-center gap-3 mt-4">
            <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold bg-red-50 px-2.5 py-1 rounded-full">
              <CheckCircle2 size={12} />
              <span
                contentEditable={editable} suppressContentEditableWarning
                onBlur={blurUpdate('purity')}
                className={editable ? 'cms-editable' : ''}
              >{product.purity}</span>
            </span>
            <span className="text-slate-400 text-xs">Research use only</span>
          </div>

          <div className="mt-6 text-4xl font-bold text-[#0a0a0a]">
            $<span
              className={editable ? 'cms-editable' : ''}
              contentEditable={editable} suppressContentEditableWarning
              onBlur={blurUpdate('price', (v) => {
                const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
                return isNaN(n) ? null : n;
              })}
            >{Number(product.price).toFixed(2)}</span>
            <span className="text-base font-medium text-slate-400 ml-2">AUD</span>
          </div>

          <p
            className={`mt-6 text-slate-600 leading-relaxed ${editable ? 'cms-editable' : ''}`}
            contentEditable={editable} suppressContentEditableWarning
            onBlur={blurUpdate('description')}
          >{product.description}</p>

          {product.in_stock ? (
            <div className="mt-8 flex items-center gap-3">
              <div className="inline-flex items-center border border-slate-200 rounded-lg bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-slate-600 hover:text-red-600" aria-label="Decrease"><Minus size={15} /></button>
                <span className="w-10 text-center font-semibold text-[#0a0a0a]">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 text-slate-600 hover:text-red-600" aria-label="Increase"><Plus size={15} /></button>
              </div>
              <button
                onClick={() => !editable && addItem({ ...product, qty }, qty)}
                disabled={editable}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
              >
                <ShoppingCart size={17} />
                Add to Cart — ${(product.price * qty).toFixed(2)}
              </button>
            </div>
          ) : (
            <button disabled className="mt-8 w-full py-3.5 bg-slate-100 text-slate-400 font-semibold rounded-lg cursor-not-allowed">Restocking Soon</button>
          )}

          <div className="mt-8 pt-8 border-t border-slate-200 grid grid-cols-1 gap-5">
            {[
              { Icon: ShieldCheck, t: '99%+ HPLC', s: 'Third-party tested' },
            ].map(({ Icon, t, s }) => (
              <div key={t} className="flex items-start gap-3">
                <Icon size={18} className="text-red-600 mt-0.5" strokeWidth={1.75} />
                <div>
                  <div className="text-sm font-semibold text-[#0a0a0a]">{t}</div>
                  <div className="text-xs text-slate-500">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="mt-24 pt-14 border-t border-slate-200">
        <h2 className="text-[#0a0a0a] font-bold text-3xl tracking-tight mb-8">You May Also Like</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {related.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </main>
  );
};

export default ProductPage;
